import { useCallback, useEffect, useMemo, useState } from 'react';

/** The slice of the server-side list contract a panel table needs. */
export interface DataTableQuery {
  search?: string;
  page: number;
  pageSize: number;
  sort?: string;
}

export interface DataTablePage<T> {
  items: T[];
  total: number;
}

export interface DataTableState<T> {
  rows: T[];
  total: number;
  page: number;
  pageCount: number;
  firstRow: number;
  lastRow: number;
  search: string;
  sort: string | null;
  loading: boolean;
  setSearch: (value: string) => void;
  toggleSort: (field: string) => void;
  setPage: (page: number) => void;
  reload: () => void;
}

/**
 * Search, sort and paging state for a `<DataTablePanel>`, fetched server-side.
 *
 * The caller supplies `fetchPage`, which receives the query and an `AbortSignal` and returns
 * one page. Everything else — resetting to page one, cycling the sort, aborting a superseded
 * request — lives here so no panel re-implements it.
 *
 * `fetchPage` must be stable (`useCallback`), or the effect re-runs on every render.
 */
export function useDataTable<T>({
  fetchPage,
  pageSize = 10,
  enabled = true,
  defaultSort = null,
}: {
  fetchPage: (query: DataTableQuery, signal: AbortSignal) => Promise<DataTablePage<T>>;
  pageSize?: number;
  /** False when the caller may not read this data at all — nothing is requested. */
  enabled?: boolean;
  defaultSort?: string | null;
}): DataTableState<T> {
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearchValue] = useState('');
  const [sort, setSort] = useState<string | null>(defaultSort);
  const [loading, setLoading] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setRows([]);
      setTotal(0);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    fetchPage(
      { search: search.trim() || undefined, page, pageSize, sort: sort ?? undefined },
      controller.signal,
    )
      .then((result) => {
        setRows(result.items);
        setTotal(result.total);
      })
      // A failure here must not take the surrounding page down; the table shows its own
      // empty line and the rest of the record still reads.
      .catch(() => {
        if (controller.signal.aborted) return;
        setRows([]);
        setTotal(0);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [fetchPage, enabled, search, page, pageSize, sort, reloadKey]);

  // A new search or sort re-reads from the first page. Staying on page 4 of a result set
  // that now has one page shows an empty table over a non-empty result.
  const setSearch = useCallback((value: string) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  /** Cycles asc → desc → unsorted, so a column can be put back the way it was found. */
  const toggleSort = useCallback((field: string) => {
    setPage(1);
    setSort((current) => {
      const [currentField, direction] = current?.split(':') ?? [];

      if (currentField !== field) return `${field}:asc`;

      return direction === 'asc' ? `${field}:desc` : null;
    });
  }, []);

  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  return useMemo(
    () => ({
      rows,
      total,
      page,
      pageCount: Math.max(1, Math.ceil(total / pageSize)),
      firstRow: total === 0 ? 0 : (page - 1) * pageSize + 1,
      lastRow: Math.min(page * pageSize, total),
      search,
      sort,
      loading,
      setSearch,
      toggleSort,
      setPage,
      reload,
    }),
    [rows, total, page, pageSize, search, sort, loading, setSearch, toggleSort, reload],
  );
}
